import {
  FETCH_ALL_USERS,
  FETCH_ALL_USERS_SUCCESS,
  FETCH_ALL_USERS_FAILED,
  EDIT_USER,
  EDIT_USER_SUCCESS,
  EDIT_USER_FAILED,
  DELETE_USER,
  DELETE_USER_SUCCESS,
  DELETE_USER_FAILED,
  FETCH_ALL_USERS_STATIC,
  FETCH_ALL_USERS_STATIC_SUCCESS,
  FETCH_ALL_USERS_STATIC_FAILED,
  USER_DELETED,
  FETCH_ALL_DRIVERS,
  FETCH_ALL_DRIVERS_SUCCESS,
  FETCH_ALL_DRIVERS_FAILED
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { onValue, set, push, update, off, get, remove, query, limitToLast } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";
import getCurrentUid from '../other/getCurrentUid';
import { safeQueryRead, safeRead } from '../other/firebaseDbHelper';

// fetchDrivers('app') short-circuits within this window to avoid re-downloading
// /users (filtered by usertype=driver) + /locations on every CreateBookingModal open.
// Pickup-radius dispatch tolerates ≤60 s of staleness because driver eligibility
// gates on push delivery + accept anyway.
const DRIVERS_CACHE_TTL_MS = 60_000;

// S2 (2026-05-26): cap fetchAllUsers/fetchUsersOnce to the most-recent N users to bound
// admin egress. Production now has 6k+ users, so the cap must stay above that size or
// older customers/drivers disappear from admin user selectors and user tables.
// Keep this as a one-shot read; the previous cost issue came from persistent full-tree
// listeners, not from explicit admin list loads.
const USERS_FETCH_LIMIT = 10000;

// S2 (2026-05-26): converted from persistent onValue on the full /users tree to a one-shot
// capped read. Persistent listener was the top RTDB egress source — re-downloaded the entire
// users tree on every child change (push token writes, queue toggles, location updates).
// New shape: parallel one-shot get() on a limited users slice + the (small) locations tree.
// Admins that need refreshed data re-dispatch this action; views already do this on mount.
export const fetchUsers = () => async (dispatch) => {

  const {
    usersRef,
    allLocationsRef
  } = firebase;

  dispatch({
    type: FETCH_ALL_DRIVERS,
    payload: null
  });

  try {
    const [usersSnap, locationsSnap] = await Promise.all([
      get(query(usersRef, limitToLast(USERS_FETCH_LIMIT))),
      get(allLocationsRef),
    ]);
    if (usersSnap.val()) {
      const locations = locationsSnap.val();
      const data = usersSnap.val();
      const arr = Object.keys(data)
        .filter(i => data[i].usertype != 'admin')
        .map(i => {
          data[i].id = i;
          data[i].location = locations && locations[i] ? locations[i] : null;
          return data[i];
        });
      dispatch({
        type: FETCH_ALL_USERS_SUCCESS,
        payload: arr,
      });
    } else {
      dispatch({
        type: FETCH_ALL_USERS_FAILED,
        payload: "No users available.",
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_ALL_USERS_FAILED,
      payload: error?.message || "Failed to fetch users.",
    });
  }
};


// Same one-shot capped read as fetchUsers, but writes to the staticusers slice and includes
// admin records. Consumed by Riders.js, Drivers.js, FleetAdmins.js, EditUser.js, etc.
export const fetchUsersOnce = (limit = USERS_FETCH_LIMIT) => async (dispatch) => {

  const {
    usersRef,
    allLocationsRef
  } = firebase;

  dispatch({
    type: FETCH_ALL_USERS_STATIC,
    payload: null
  });

  try {
    const usersQuery = limit ? query(usersRef, limitToLast(limit)) : usersRef;
    const [usersSnap, locationsSnap] = await Promise.all([
      get(usersQuery),
      get(allLocationsRef),
    ]);
    if (usersSnap.val()) {
      const locations = locationsSnap.val();
      const data = usersSnap.val();
      const arr = Object.keys(data)
        .map(i => {
          data[i].id = i;
          data[i].location = locations && locations[i] ? locations[i] : null;
          return data[i];
        });
      dispatch({
        type: FETCH_ALL_USERS_STATIC_SUCCESS,
        payload: arr,
      });
    } else {
      dispatch({
        type: FETCH_ALL_USERS_STATIC_FAILED,
        payload: "No users available.",
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_ALL_USERS_STATIC_FAILED,
      payload: error?.message || "Failed to fetch users.",
    });
  }
};
export const clearFetchDrivers = () => (dispatch) => {
  const {
    driversRef,
    allLocationsRef,
  } = firebase;
  off(driversRef);
  off(allLocationsRef);
}

export const fetchDrivers = (appType) => async(dispatch, getState) => {

  const {
    driversRef,
    allLocationsRef,
    settingsRef,
  } = firebase;

  // Cache check: only on mobile app path. Web admin keeps its persistent listener
  // because dashboards need live driver/location data and an admin session is long-lived.
  if (appType === 'app' && typeof getState === 'function') {
    const cached = getState().usersdata;
    if (cached && Array.isArray(cached.drivers)
        && Date.now() - (cached.driversFetchedAt || 0) < DRIVERS_CACHE_TTL_MS) {
      const action = {
        type: FETCH_ALL_DRIVERS_SUCCESS,
        payload: cached.drivers,
        fromCache: true,
      };
      dispatch(action);
      return action;
    }
  }

  const settingsdata = await get(settingsRef);
  const settings = settingsdata.val();

  dispatch({
    type: FETCH_ALL_DRIVERS,
    payload: null
  });

  const buildDriversPayload = (data, locations, settings) => {
    return Object.keys(data)
      .filter(i => {
        const driver = data && data[i] ? data[i] : null;
        const hasLocation = !!(locations && locations[i]);
        if (!driver || !hasLocation || driver.driverActiveStatus !== true) {
          return false;
        }
        if (settings && settings.driver_approval && driver.approved !== true) {
          return false;
        }
        if (settings && settings.license_image_required && !driver.licenseImage) {
          return false;
        }
        if (settings && settings.imageIdApproval && !driver.verifyIdImage) {
          return false;
        }
        if (settings && settings.carType_required && !driver.carType) {
          return false;
        }
        if (settings && settings.carApproval && driver.carApproved !== true) {
          return false;
        }
        return true;
      })
      .map(i => {
        return {
          id: i,
          uid: i,
          location: locations && locations[i] ? locations[i]:null,
          carType: data[i].carType ? data[i].carType : null,
          vehicleNumber:  data[i].vehicleNumber ? data[i].vehicleNumber : null,
          fleetadmin:  data[i].fleetadmin ? data[i].fleetadmin : null,
          firstName: data[i].firstName,
          lastName: data[i].lastName,
          mobile: data[i].mobile ? data[i].mobile : "",
          email: data[i].email ? data[i].email : "",
          pushToken: data[i].pushToken ? data[i].pushToken : "",
          queue: data[i].queue
        };
      });
  };

  if (appType === 'app') {
    try {
      const [driverSnapshot, locationSnapshot] = await Promise.all([
        get(driversRef),
        get(allLocationsRef),
      ]);
      const data = driverSnapshot.val();
      const locations = locationSnapshot.val();

      if (data) {
        const arr = buildDriversPayload(data, locations, settings);
        const action = {
          type: FETCH_ALL_DRIVERS_SUCCESS,
          payload: arr
        };
        dispatch(action);
        return action;
      }

      const action = {
        type: FETCH_ALL_DRIVERS_FAILED,
        payload: []
      };
      dispatch(action);
      return action;
    } catch (error) {
      console.warn('[drivers] Primary driver fetch failed, trying REST fallback.', {
        error: error?.message || String(error)
      });

      try {
        const [data, locations] = await Promise.all([
          safeQueryRead('/users', 'usertype', 'driver', true),
          safeRead('/locations', true),
        ]);

        if (data) {
          const arr = buildDriversPayload(data, locations, settings);
          const action = {
            type: FETCH_ALL_DRIVERS_SUCCESS,
            payload: arr
          };
          dispatch(action);
          return action;
        }
      } catch (restError) {
        console.error('[drivers] REST fallback failed.', {
          error: restError?.message || String(restError)
        });
      }

      const action = {
        type: FETCH_ALL_DRIVERS_FAILED,
        payload: []
      };
      dispatch(action);
      return action;
    }
  }

  return await new Promise((resolve) => {
    onValue(driversRef, snapshot => {
      if (snapshot.val()) {
        onValue(allLocationsRef, locres=>{
          const locations = locres.val();
            const data = snapshot.val();
            const arr = buildDriversPayload(data, locations, settings);
            dispatch({
              type: FETCH_ALL_DRIVERS_SUCCESS,
              payload: arr
            });
            resolve({
              type: FETCH_ALL_DRIVERS_SUCCESS,
              payload: arr
            });
          }, appType === 'app' ? {onlyOnce: true} : settings && settings.realtime_drivers ? null : {onlyOnce: true})
      } else {
        dispatch({
          type: FETCH_ALL_DRIVERS_FAILED,
          payload: "No users available."
        });
        resolve({
          type: FETCH_ALL_DRIVERS_FAILED,
          payload: []
        });
      }
    }, error => {
      console.error('[drivers] Realtime driver listener failed.', {
        error: error?.message || String(error)
      });
      dispatch({
        type: FETCH_ALL_DRIVERS_FAILED,
        payload: []
      });
      resolve({
        type: FETCH_ALL_DRIVERS_FAILED,
        payload: []
      });
    },appType === 'app'? {onlyOnce: true}: settings && settings.realtime_drivers ? null : {onlyOnce: true});
  });
};

export const addUser = (userdata) => (dispatch) => {
  const {
    usersRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: userdata
  });

  delete userdata.tableData;

  push(usersRef, userdata).then(() => {
    dispatch({
      type: EDIT_USER_SUCCESS,
      payload: null
    });
  }).catch((error) => {
    dispatch({
      type: EDIT_USER_FAILED,
      payload: error
    });
  });
}

export const editUser = (id, user) => (dispatch) => {

  const {
    singleUserRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: user
  });
  let editedUser = user;
  delete editedUser.id;
  delete editedUser.tableData;
  set(singleUserRef(id), editedUser);
}

export const updateUserCar = (id, data) => (dispatch) => {
  const {
    singleUserRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: data  
  });
  update(singleUserRef(id),data);
}

export const updateLicenseImage = (uid, imageBlob, imageType) => async (dispatch) => {

  const {
    singleUserRef,
    driverDocsRef,
    driverDocsRefBack,
    verifyIdImageRef
  } = firebase;

  let profile = {};
  if(imageType === 'licenseImage'){
    await uploadBytesResumable(driverDocsRef(uid),imageBlob);
    let image = await getDownloadURL(driverDocsRef(uid));
    profile.licenseImage = image;
  }
  if(imageType === 'licenseImageBack'){
    await uploadBytesResumable(driverDocsRefBack(uid),imageBlob);
    let image1 = await getDownloadURL(driverDocsRefBack(uid));
    profile.licenseImageBack = image1;
  }
  if(imageType === 'verifyIdImage'){
    await uploadBytesResumable(verifyIdImageRef(uid),imageBlob);
    let image1 = await getDownloadURL(verifyIdImageRef(uid));
    profile.verifyIdImage = image1;
  }
  await update(singleUserRef(uid), profile);
  dispatch({
    type: EDIT_USER,
    payload: uid
  });
};

export const deleteUser = (uid) => (dispatch) => {

  const {
    auth,
    walletHistoryRef,
    singleUserRef,
    userNotificationsRef,
    carsRef,
    carEditRef
  } = firebase;

  dispatch({
    type: DELETE_USER,
    payload: uid
  });

  if (getCurrentUid(auth) === uid) {
    off(singleUserRef(uid));
    off(walletHistoryRef(uid));
    off(userNotificationsRef(uid));
  }

  onValue(singleUserRef(uid), userdata => {
    const profile = userdata.val();
    if(profile.usertype === 'driver'){
      onValue(carsRef(uid, profile.usertype), carssnapshot => {
        let cars = carssnapshot.val();
        if (cars) {
          const arr = Object.keys(cars);
          for(let i = 0; i < arr.length; i++){
            remove(carEditRef(arr[i]));
          }
        }
      });
    } 
    
    remove(singleUserRef(uid)).then(() => {
      if (getCurrentUid(auth) === uid) {
        signOut(auth);
        dispatch({
          type: USER_DELETED,
          payload: null
        });
      } else {
        remove(singleUserRef(uid)).then(() => {
          dispatch({
            type: DELETE_USER_SUCCESS,
            payload: null
          });
        }).catch((error) => {
          dispatch({
            type: DELETE_USER_FAILED,
            payload: error
          });
        });
      }
    });
  },{onlyOnce:true});
}
